import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { User, TeamDetailed, AssignUserData } from '@/types';

interface AssignUserModalProps {
  user: User;
  teams: TeamDetailed[];
  onClose: () => void;
  onAssign: (assignmentData: AssignUserData) => Promise<boolean>;
}

/**
 * Modal component for assigning users to teams
 */
const AssignUserModal: React.FC<AssignUserModalProps> = ({ user, teams, onClose, onAssign }) => {
  const { isDark } = useTheme();
  const [selectedTeam, setSelectedTeam] = useState<number>(0);
  const [selectedRole, setSelectedRole] = useState<'coach' | 'athlete'>('athlete');
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selectedTeam) {
      alert('Please select a team');
      return;
    }

    try {
      setLoading(true);
      const success = await onAssign({
        user_id: user.id,
        team_id: selectedTeam,
        role: selectedRole
      });
      if (success) {
        onClose();
      } else {
        alert('Failed to assign user to team. Please try again.');
      }
    } catch (error) {
      console.error('Error assigning user to team:', error);
      alert('An error occurred while assigning the user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
         <div className="fixed inset-0 bg-gray-800/80 dark:bg-gray-950/80 overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
                         <h3 className="text-2xl cgm-text-primary mb-2">
               Assign User to Team
             </h3>
            <p className="cgm-text-secondary text-sm">
              Assign {user.full_name || user.username} to a team
            </p>
          </div>
          
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <div className="text-sm cgm-text-secondary">
              <div className="mb-2">
                <span className="font-medium cgm-text-primary">User:</span> {user.full_name || user.username}
              </div>
              <div>
                <span className="font-medium cgm-text-primary">Email:</span> {user.email}
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="team" className="block text-sm font-medium cgm-text-secondary mb-1">
                Team
              </label>
              <select
                id="team"
                required
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(parseInt(e.target.value))}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value={0}>Select a team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.organization.name})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="role" className="block text-sm font-medium cgm-text-secondary mb-1">
                Role
              </label>
              <select
                id="role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as 'coach' | 'athlete')}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Assigning User...
                </>
              ) : (
                'Assign User'
              )}
            </button>
          </form>

          {/* Navigation Links */}
          <div className="mt-6 space-y-4 text-center">
                         <button
               onClick={onClose}
               className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
             >
               Cancel
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignUserModal; 